/**
 * bootstrapConversation — creates a backend ESA conversation and wires it into
 * the host ESA primitives so that SolutionRunCard.sendMessage() routes to it.
 *
 * WHY here, not in useEsaConverse (DR-12):
 * - ESAStoreContract must not be extended with host-internal orchestration.
 * - The three host-internal primitives (createConversation, setActiveThread,
 *   MessageCache.addMessage) are already used together inside useEsaConverse.
 * - Reusing them here gives identical useEffect sync behaviour: after
 *   setActiveThread(realId), sessionIdRef.current in useEsaConverse becomes realId
 *   so subsequent sendMessage() calls route to the pre-created conversation.
 */

import { createConversation } from '../../Esa/services/esaApi';
import { useEsaDomainStore } from '../../Esa/store/esaDomainStore';
import { getMessageCache } from '../../Esa/cache/messageCacheInstance';
import { useShellStore } from '../../../shell/store/shellStore';
import type { ESACacheMessage } from '../../Esa/cache/MessageCache';
import type { InvokeSolutionPayload, SolutionOption } from '../types/solutionRun';

/** Builds the frontend-generated opening message seeded in MessageCache (DR-4). */
export function buildOpeningMessage(solution: SolutionOption, realId: string): ESACacheMessage {
  return {
    id: `sys-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    conversationId: realId,
    parentMessageId: null,
    initiator: 'system',
    messageType: 'text',
    message: {
      text:
        `Let's set up a new **${solution.label}** run. ` +
        `Tell me the **objective** — what outcome do you want? ` +
        `You can also fill the form on the canvas; everything stays in sync.`,
    },
    hasAttachments: false,
    storedFileUkey: null,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Creates a new ESA conversation for the solution run and wires it into the
 * existing host primitives. Returns the real conversation id on success.
 *
 * AR2-003 (resolved): The overlay is rendered optimistically BEFORE this call;
 * the SolutionRunCard shows a loading skeleton until conversationId is set.
 */
export async function bootstrapConversation(
  payload: InvokeSolutionPayload
): Promise<string> {
  const shell = useShellStore.getState();
  const solution =
    payload.solutions.find(s => s.id === payload.defaultSolutionId) ??
    payload.solutions[0];

  const { id: realId } = await createConversation('New solution run', {
    tenantId: shell.tenant?.id,
    userId: shell.user?.id,
  });

  const esaStore = useEsaDomainStore.getState();
  // setActiveThread syncs sessionIdRef.current in all useEsaConverse instances
  esaStore.setActiveThread(realId);
  esaStore.upsertThread({
    id: realId,
    title: 'New solution run',
    lastUpdatedAt: Date.now(),
    hasActiveHITL: false,
    status: 'active',
  });

  const openingMessage = buildOpeningMessage(solution, realId);
  getMessageCache().addMessage(realId, openingMessage);

  return realId;
}
